import type { IErrorTracker, ErrorContext, SeverityLevel, CaptureResult } from '../interfaces/IErrorTracker';
import { resolveSentryEnvironment, scrubSentryEvent } from '../sentry';

/**
 * Routes whose request body is a credential or identity PII, so the SDK never
 * reads it at all — the key filter in `scrubSentryEvent` is the second belt,
 * not the only one. Each pattern is the route's full path under the prefix
 * `apps/api/server/server.ts` mounts it at:
 *
 *   - `/mailbox`                               a rep's mailbox app password
 *   - `/api/v1/payroll/me/ssn`                 an employee's SSN and date of birth
 *   - `/api/v1/payroll/employee-private`       SSN, bank account, wages (manager / W-4)
 *   - `/api/v1/employees/me/pin`               a POS PIN, current and new
 *   - `/api/v1/gift-cards/:cardGuid/pin`       a gift card's spending PIN
 */
const SENSITIVE_REQUEST_BODY_PATHS: readonly RegExp[] = [
    /\/mailbox(?:[/?#]|$)/,
    /\/api\/v1\/payroll\/me\/ssn(?:[/?#]|$)/,
    /\/api\/v1\/payroll\/employee-private(?:[/?#]|$)/,
    /\/api\/v1\/employees\/me\/pin(?:[/?#]|$)/,
    /\/api\/v1\/gift-cards\/[^/?#]+\/pin(?:[/?#]|$)/,
];

export function isSensitiveRequestBodyUrl(url: string): boolean {
    return SENSITIVE_REQUEST_BODY_PATHS.some((pattern) => pattern.test(url));
}

/**
 * Optional configuration injected by callers that cannot read `process.env`
 * at runtime — notably the Vite SPA bundles (onboarding / menu / checkout),
 * where `process.env.SENTRY_DSN` does NOT exist and is therefore permanently
 * empty. Those callers resolve their values from `import.meta.env.VITE_*`
 * and pass them in via the constructor and/or `initializeWithModule`.
 *
 * Any field left undefined falls back to the existing `process.env` reads,
 * so the Next.js / Node consumers (which DO have `process.env`) are unchanged.
 */
export interface SentryTrackerConfig {
    dsn?: string;
    environment?: string;
    release?: string;
    sampleRate?: number;
    tracesSampleRate?: number;
}

/**
 * Sentry Error Tracker
 *
 * Works with both @sentry/node and @sentry/react.
 * Lazy-loads the Sentry SDK to avoid hard dependency.
 *
 * DSN/env/release/sampleRate resolve in this order:
 *   1. Values injected via the constructor or `initializeWithModule(..., config)`
 *      (used by the Vite SPAs, which read `import.meta.env.VITE_*`).
 *   2. The corresponding `process.env.*` vars (Next.js / Node consumers).
 *
 * Required (one of):
 * - injected `config.dsn`, or
 * - env var SENTRY_DSN (or NEXT_PUBLIC_SENTRY_DSN)
 * - SENTRY_RELEASE (optional)
 *
 * Install: pnpm add @sentry/node (server) or @sentry/react (client)
 */
export class SentryErrorTracker implements IErrorTracker {
    readonly providerName = 'Sentry';
    private sentry: any = null;
    private readonly _app: string;
    private dsn: string;
    private readonly config: SentryTrackerConfig;

    constructor(app: string, config?: SentryTrackerConfig) {
        this._app = app;
        this.config = config ?? {};
        this.dsn = this.config.dsn
            || process.env.SENTRY_DSN
            || process.env.NEXT_PUBLIC_SENTRY_DSN
            || '';
    }

    isConfigured(): boolean {
        return !!this.dsn && this.sentry !== null;
    }

    /**
     * Initialize Sentry. Must be called at app startup.
     * Pass the Sentry module directly to avoid this package
     * taking a hard dependency on either @sentry/node or @sentry/react.
     *
     * An optional `config` lets callers inject DSN/env/release/sampleRate when
     * `process.env` is unavailable (Vite bundles). Injected values override the
     * constructor config; both fall back to `process.env`.
     *
     * Double-init guard: if the passed module already has an initialized client
     * (`sentryModule.getClient?.()` is truthy — e.g. the Vite `main.tsx` calls
     * `Sentry.init` directly), the module is simply attached so this tracker can
     * route through it, WITHOUT calling `init()` again.
     *
     * @example
     * ```
     * import * as Sentry from '@sentry/node';
     * const tracker = new SentryErrorTracker('api');
     * tracker.initializeWithModule(Sentry);
     * ```
     */
    initializeWithModule(sentryModule: any, config?: SentryTrackerConfig): void {
        this.sentry = sentryModule;

        // Allow late injection (Vite path resolves `import.meta.env` at the
        // boundary, not at construction). Injected DSN wins over env fallback.
        if (config?.dsn) this.dsn = config.dsn;

        if (!this.dsn) return;

        // If the host already ran `Sentry.init(...)` (the Vite SPAs do this in
        // their `main.tsx`), reuse the existing client instead of re-initializing.
        // We just needed `this.sentry` set above so capture methods route through
        // the live SDK and `isConfigured()` flips to true.
        if (typeof sentryModule.getClient === 'function' && sentryModule.getClient()) {
            return;
        }

        const resolved: SentryTrackerConfig = { ...this.config, ...config };

        // The order is the whole point, and the FIRST candidate is a variable
        // that exists for no other purpose.
        //
        // NODE_ENV is useless here: Vercel sets it to `production` for preview
        // and custom-environment builds too, so a NODE_ENV-first resolution
        // labels every deployment production. VERCEL_ENV is better but still
        // ambient — on the client it depends on a per-project "expose system
        // environment variables" toggle nothing here asserts, and what it
        // reports for a CUSTOM environment (our real `dev` tier) is Vercel's
        // choice rather than ours. `preview` is a value this policy DROPS, so
        // trusting it could discard a whole tier instead of mislabelling one.
        //
        // SENTRY_ENVIRONMENT is written per target by
        // scripts/ops/sentry-provision.ts. It is deliberately NOT APP_ENV:
        // that name is already load-bearing elsewhere (apps/restaurant's proxy
        // and pageview beacon, apps/oidc-provider's dev-redirect gate), each
        // with its own "absent means development" fallback, so writing it for
        // Sentry's benefit would change routing and logging behaviour that has
        // nothing to do with Sentry. APP_ENV stays as a second candidate
        // because apps/functions genuinely sets it as its tier.
        const environment = resolved.environment
            || resolveSentryEnvironment(
                process.env.SENTRY_ENVIRONMENT,
                process.env.APP_ENV,
                process.env.VERCEL_ENV,
                process.env.NODE_ENV,
            );

        this.sentry.init({
            dsn: this.dsn,
            environment,
            release: resolved.release || process.env.SENTRY_RELEASE || undefined,
            sampleRate: resolved.sampleRate
                ?? parseFloat(process.env.SENTRY_SAMPLE_RATE || '1.0'),
            tracesSampleRate: resolved.tracesSampleRate
                ?? parseFloat(process.env.SENTRY_TRACES_SAMPLE_RATE || '0.1'),
            initialScope: {
                tags: { app: this._app },
            },
            // Audit-11 drive-by (Initiative #3 PR 8): default-deny PII so
            // wrapping apps can't accidentally surface emails / usernames
            // via Sentry's auto-detected user context.
            sendDefaultPii: false,
            // Shared policy (`../sentry`): drops preview/dev/test events so they
            // cannot pollute a production project, and strips the identity
            // fields the SDK auto-populates from request context. It lives in
            // one place because it was copy-pasted into thirty config files and
            // they had already drifted — the custom Vercel environment named
            // `dev` matched none of the inline copies.
            beforeSend: (event: unknown) =>
                scrubSentryEvent(event as Parameters<typeof scrubSentryEvent>[0], environment),
            // Review F13: @sentry/node attaches an incoming request's BODY to an
            // event. `scrubSentryEvent` redacts secret-named keys in it for every
            // route; the mailbox routes' bodies (an app password) are not even
            // read. A browser SDK has no httpIntegration and keeps its defaults.
            ...(typeof this.sentry.httpIntegration === 'function'
                ? {
                    integrations: (defaults: Array<{ name: string }>) => defaults.map((integration) =>
                        integration.name === 'Http'
                            ? this.sentry.httpIntegration({ ignoreIncomingRequestBody: isSensitiveRequestBodyUrl })
                            : integration),
                }
                : {}),
        });
    }

    captureException(error: Error | unknown, context?: ErrorContext): CaptureResult {
        if (!this.isConfigured()) {
            return { success: false, error: 'Sentry is not configured' };
        }

        const eventId = this.sentry.captureException(error, {
            contexts: { custom: context as Record<string, unknown> },
            tags: this.contextToTags(context),
        });

        return { success: true, eventId };
    }

    captureMessage(message: string, level?: SeverityLevel, context?: ErrorContext): CaptureResult {
        if (!this.isConfigured()) {
            return { success: false, error: 'Sentry is not configured' };
        }

        const eventId = this.sentry.captureMessage(message, {
            level: level ?? 'info',
            contexts: { custom: context as Record<string, unknown> },
            tags: this.contextToTags(context),
        });

        return { success: true, eventId };
    }

    setUser(user: { id: string; email?: string; tenantId?: string; restaurantId?: string } | null): void {
        this.sentry?.setUser(user);
    }

    setTag(key: string, value: string): void {
        this.sentry?.setTag(key, value);
    }

    addBreadcrumb(breadcrumb: {
        category: string;
        message: string;
        level?: SeverityLevel;
        data?: Record<string, unknown>;
    }): void {
        this.sentry?.addBreadcrumb({
            category: breadcrumb.category,
            message: breadcrumb.message,
            level: breadcrumb.level ?? 'info',
            data: breadcrumb.data,
        });
    }

    startTransaction(name: string, op: string): { finish: () => void } {
        if (!this.isConfigured()) return { finish: () => {} };

        const span = this.sentry.startInactiveSpan({ name, op });
        return { finish: () => span?.end() };
    }

    async flush(timeout = 5000): Promise<boolean> {
        if (!this.isConfigured()) return true;
        return this.sentry.flush(timeout);
    }

    private contextToTags(context?: ErrorContext): Record<string, string> {
        if (!context) return {};
        const tags: Record<string, string> = {};
        if (context.component) tags['component'] = context.component;
        if (context.operation) tags['operation'] = context.operation;
        if (context.tenantId) tags['tenant_id'] = context.tenantId;
        if (context.restaurantId) tags['restaurant_id'] = context.restaurantId;
        if (context.userId) tags['user_id'] = context.userId;
        return tags;
    }
}
